import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

export function findMatchedPlantForRecommendation(matchedPlants, plant) {
  if (!matchedPlants?.length) return null;
  return matchedPlants.find(
    (mp) =>
      mp.ai_recommendation?.plant_name === plant.plant_name ||
      mp.ai_recommendation?.common_name === plant.plant_name
  );
}

export function googleImageSearchUrl(query) {
  return `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(query)}`;
}

/**
 * Structured AI output: plants, design ideas, yard opportunities, next steps (same as Get Ideas).
 */
export default function AiIdeasAnalysisSections({
  analysis,
  matchedPlants,
  maxPlantRecommendations = 6,
  maxDesignSuggestions = 4,
}) {
  if (!analysis) return null;

  return (
    <div className="space-y-4">
      {analysis.plant_recommendations?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Plant suggestions</h3>
          <ul className="space-y-4">
            {analysis.plant_recommendations.slice(0, maxPlantRecommendations).map((p, i) => {
              const matched = findMatchedPlantForRecommendation(matchedPlants, p);
              const pd = matched?.plant_data;
              const imageQuery = [p.plant_name, p.botanical_name].filter(Boolean).join(' ');
              return (
                <li key={i} className="text-sm border-b border-gray-100 last:border-0 last:pb-0 pb-4">
                  <div className="flex gap-3">
                    {pd?.image_url ? (
                      <img
                        src={pd.image_url}
                        alt=""
                        className="w-20 h-20 rounded-lg object-cover border border-gray-200 flex-shrink-0 bg-gray-50"
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{p.plant_name}</span>
                      {p.botanical_name && (
                        <span className="text-gray-400 italic ml-1">({p.botanical_name})</span>
                      )}
                      {p.care_level && (
                        <span
                          className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${
                            p.care_level === 'Low'
                              ? 'bg-green-100 text-green-800'
                              : p.care_level === 'High'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {p.care_level} care
                        </span>
                      )}
                      {pd?.common_name && (
                        <span className="ml-2 text-xs text-green-800 font-medium">In PlantADay library</span>
                      )}
                      {p.reason && <p className="text-gray-600 mt-0.5 leading-snug">{p.reason}</p>}
                      {p.placement && (
                        <p className="text-gray-500 text-xs mt-0.5">Placement: {p.placement}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        {pd?.common_name && (
                          <Link
                            to={`/plants?search=${encodeURIComponent(pd.common_name)}`}
                            className="text-xs font-semibold text-blue-700 hover:underline"
                          >
                            View in plant library
                          </Link>
                        )}
                        <a
                          href={googleImageSearchUrl(`${imageQuery} garden plant`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-gray-600 hover:text-gray-900 hover:underline inline-flex items-center gap-0.5"
                        >
                          Image search
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {analysis.design_suggestions?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Design ideas</h3>
          <ul className="space-y-3">
            {analysis.design_suggestions.slice(0, maxDesignSuggestions).map((s, i) => (
              <li key={i} className="text-sm">
                <p className="font-medium text-gray-900">{s.suggestion}</p>
                {s.location && <p className="text-gray-500 text-xs">Where: {s.location}</p>}
                {s.benefits && <p className="text-gray-600 mt-0.5 leading-snug">{s.benefits}</p>}
                <a
                  href={googleImageSearchUrl(`${s.suggestion} landscape garden`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-0.5 text-xs font-semibold text-gray-600 hover:text-gray-900 hover:underline"
                >
                  Image search for inspiration
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysis.current_state?.opportunities?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Opportunities in your yard</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            {analysis.current_state.opportunities.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        </div>
      )}

      {analysis.next_steps && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-2">Next steps</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{analysis.next_steps}</p>
        </div>
      )}
    </div>
  );
}
